<script setup lang="ts">
  import { AsYouType, parsePhoneNumberWithError } from 'libphonenumber-js';
  import type { CountryCode, Extension, PhoneNumber } from 'libphonenumber-js';
  import { ChevronsUpDown } from 'lucide-vue-next';
  import { computed, nextTick, onBeforeMount, ref, watch } from 'vue';
  import type { HTMLAttributes } from 'vue';
  import 'flag-icons/css/flag-icons.min.css';

  import type { Country } from '@stuut/api/models';
  import { stuut } from '@stuut/dashboard/src/api';
  import { Button } from '@ui/components/button';
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
  } from '@ui/components/command';
  import {
    InputGroup,
    InputGroupAddon,
    InputGroupInput,
    InputGroupText,
  } from '@ui/components/input-group';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';

  export type { PhoneNumber };

  const props = defineProps<{
    defaultValue?: string;
    modelValue?: string | null;
    placeholder?: string;
    class?: HTMLAttributes['class'];
  }>();
  const emit = defineEmits<{
    (e: 'update:modelValue', payload: string): void;
    (
      e: 'change',
      payload: {
        phoneNumber: PhoneNumber | undefined;
        isValidNumber: boolean;
        raw: string | undefined;
        ext: string | undefined;
      },
    ): void;
  }>();

  const countries = ref<Country[]>([]);

  const isLoading = ref(false);

  const countryMap = computed<Record<string, Country>>(() => {
    return countries.value.reduce((m, country) => {
      m[country.isoCode] = country;
      return m;
    }, {});
  });

  const selectedCountryCode = ref<CountryCode>('US');
  const asYouType = ref<AsYouType>(new AsYouType(selectedCountryCode.value));
  const phoneNumberRawInput = ref('');
  const rawExtensionInput = ref<Extension>();
  const originalNumber = ref<PhoneNumber>();
  const countrySelectorOpen = ref(false);

  const handleCountrySelect = (isoCode: CountryCode) => {
    selectedCountryCode.value = isoCode;
    countrySelectorOpen.value = false;
  };

  watch(selectedCountryCode, (newCountry) => {
    asYouType.value = new AsYouType(newCountry);
  });

  // Compute the parsed phone number from the raw input and country code
  const phoneNumber = computed(() => {
    try {
      const parsedNumber = parsePhoneNumberWithError(
        phoneNumberRawInput.value,
        selectedCountryCode.value,
      );
      if (parsedNumber && rawExtensionInput.value) {
        parsedNumber.setExt(rawExtensionInput.value);
      }
      return parsedNumber;
    } catch {
      return undefined;
    }
  });

  const isValidNumber = computed(() => {
    try {
      const numberCountry = phoneNumber.value?.country?.toUpperCase();
      const selectedCountry = selectedCountryCode.value?.toUpperCase();

      // Hacky check since US and CA share the same country code
      const isNaPair =
        (numberCountry === 'US' || numberCountry === 'CA') &&
        (selectedCountry === 'US' || selectedCountry === 'CA');

      const isCountryMatch = isNaPair || numberCountry === selectedCountry;

      return Boolean(isCountryMatch && phoneNumber.value?.isValid());
    } catch {
      return false;
    }
  });

  const isNumberChanged = computed(() => {
    return phoneNumber.value?.formatInternational() !== originalNumber.value?.formatInternational();
  });

  // Watch raw input too so consumers are notified when the user edits the
  // field but the parsed phoneNumber computed stays undefined (e.g. backspacing
  // through invalid intermediate states down to an empty string).
  watch([phoneNumber, phoneNumberRawInput], ([newPhoneNumber]) => {
    emit('update:modelValue', newPhoneNumber?.formatInternational() ?? phoneNumberRawInput.value);
    emit('change', {
      ext: rawExtensionInput.value || undefined,
      isValidNumber: isValidNumber.value,
      phoneNumber: newPhoneNumber,
      raw: phoneNumberRawInput.value,
    });
  });

  const handleInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const newValue = input.value;
    const selectionStart = input.selectionStart;

    // Try libphonenumber first — it handles E.164 pastes, various extension
    // formats (;ext=, x, ext., extension), and country inference from the +
    // prefix. Falls through to AsYouType for in-progress typing that parse fails.
    const tryParse = (text: string) => {
      try {
        return parsePhoneNumberWithError(text, {
          defaultCountry: selectedCountryCode.value,
          extract: true,
        });
      } catch {
        return undefined;
      }
    };

    let parsed = tryParse(newValue);

    // Fallback for international numbers pasted without a + (e.g. "44 203 048 4377").
    // If parse didn't produce a country and the raw digits are longer than a
    // national number (>10), retry with a + prepended so the parser can extract
    // a country calling code.
    if (!parsed?.country) {
      const digitsOnly = newValue.replace(/\D/g, '');
      if (digitsOnly.length > 10 && !digitsOnly.startsWith('1')) {
        parsed = tryParse(`+${digitsOnly}`);
      }
    }

    if (parsed?.country) {
      selectedCountryCode.value = parsed.country;
      asYouType.value = new AsYouType(parsed.country);
      const formatted = asYouType.value.input(parsed.nationalNumber);
      phoneNumberRawInput.value = formatted;
      // When the pasted string normalizes to the same formatted value that's
      // already in the ref (e.g. pasting the same number twice), Vue's
      // v-model diff is a no-op and the DOM input keeps the raw pasted text.
      // Sync the DOM directly to guarantee the user sees the formatted value.
      input.value = formatted;
      rawExtensionInput.value = (parsed.ext || '') as Extension;
      return;
    }

    // Strip all formatting to get just the digits
    const digits = newValue.replace(/\D/g, '');

    // Use AsYouType to format the digits
    asYouType.value.reset();
    const formattedNumber = asYouType.value.input(digits);

    phoneNumberRawInput.value = formattedNumber;

    // Calculate new cursor position
    nextTick(() => {
      if (selectionStart !== null) {
        // Find the number of non-digit characters before the cursor in the old value
        const oldValueBeforeCursor = newValue.slice(0, selectionStart);
        const oldDigitsBeforeCursor = oldValueBeforeCursor.replace(/\D/g, '').length;

        // Find the position in the new formatted string that corresponds to the same number of digits
        let newPosition = 0;
        let digitCount = 0;
        while (digitCount < oldDigitsBeforeCursor && newPosition < formattedNumber.length) {
          if (/\d/.test(formattedNumber[newPosition])) {
            digitCount++;
          }
          newPosition++;
        }

        input.setSelectionRange(newPosition, newPosition);
      }
    });
  };

  const handleExtInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const selectionStart = input.selectionStart;

    rawExtensionInput.value = input.value.replace(/\D/g, '').slice(0, 10) as Extension;

    emit('change', {
      ext: rawExtensionInput.value || undefined,
      isValidNumber: isValidNumber.value,
      phoneNumber: phoneNumber.value,
      raw: phoneNumberRawInput.value,
    });

    nextTick(() => {
      if (selectionStart !== null) {
        input.setSelectionRange(selectionStart, selectionStart);
      }
    });
  };

  // Initialize from props
  onBeforeMount(async () => {
    isLoading.value = true;

    countries.value = await stuut.phoneNumbers.getSupportedCountries();

    if (props.modelValue || props.defaultValue) {
      try {
        const parsedNumber = parsePhoneNumberWithError(
          props.modelValue || props.defaultValue || '',
          {
            extract: true,
          },
        );
        originalNumber.value = parsedNumber;

        selectedCountryCode.value = parsedNumber.country || 'US';
        asYouType.value = new AsYouType(selectedCountryCode.value);
        // Format just the national digits through AsYouType so the extension
        // (which parsedNumber.format('NATIONAL') would append) stays out of the main field.
        phoneNumberRawInput.value = asYouType.value.input(parsedNumber.nationalNumber);
        if (parsedNumber.ext) {
          rawExtensionInput.value = parsedNumber.ext;
        }
      } catch (e) {
        console.error('Error parsing phone number ', e);
      }
    }

    isLoading.value = false;
  });
</script>

<template>
  <InputGroup
    :class="props.class"
    :aria-invalid="Boolean(phoneNumber && isNumberChanged && !isValidNumber)"
  >
    <InputGroupAddon
      align="inline-start"
      class="has-[>button]:ml-0 self-stretch overflow-hidden rounded-l-md border-r border-border p-0"
    >
      <Popover v-model:open="countrySelectorOpen">
        <PopoverTrigger as-child>
          <Button
            type="button"
            variant="ghost"
            :disabled="isLoading"
            role="combobox"
            :aria-expanded="countrySelectorOpen"
            aria-label="Select country"
            class="h-full w-full gap-2 rounded-none border-0 px-2 py-0 shadow-none hover:bg-accent/50 focus-visible:ring-0"
          >
            <i
              class="fi shadow-sm"
              :class="`fi-${selectedCountryCode.toLowerCase()}`"
              aria-hidden="true"
            />
            <ChevronsUpDown class="size-3" />
          </Button>
        </PopoverTrigger>

        <PopoverContent class="w-[300px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search country..." />
            <CommandList>
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  v-for="country in countries"
                  :key="country.isoCode"
                  :value="`${country.name} +${country.callingCode} ${country.isoCode}`"
                  class="gap-2"
                  @select="handleCountrySelect(country.isoCode as CountryCode)"
                >
                  <i
                    class="fi text-sm"
                    :class="`fi-${country.isoCode.toLowerCase()}`"
                    aria-hidden="true"
                  />
                  <span class="flex-1 text-xs font-medium">{{ country.name }}</span>
                  <span class="text-xs"> +{{ country.callingCode }} </span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </InputGroupAddon>

    <InputGroupAddon align="inline-start">
      <InputGroupText> +{{ countryMap[selectedCountryCode]?.callingCode }} </InputGroupText>
    </InputGroupAddon>

    <InputGroupInput
      v-model="phoneNumberRawInput"
      :disabled="isLoading"
      :placeholder="placeholder || '(555) 123-4567'"
      type="text"
      @input="handleInput"
    />

    <InputGroupAddon
      align="inline-end"
      class="self-stretch gap-1 border-l border-border py-0 pl-2 font-normal"
    >
      <InputGroupText class="font-normal"> ext. </InputGroupText>
      <InputGroupInput
        :model-value="rawExtensionInput"
        :disabled="isLoading"
        placeholder="1234"
        type="text"
        inputmode="numeric"
        class="w-16 font-normal"
        :class="[rawExtensionInput ? 'text-foreground' : '']"
        @input="handleExtInput"
        @click.stop
      />
    </InputGroupAddon>
  </InputGroup>
</template>

<style lang="postcss" scoped>
  @reference "../../globals.css";
  /* Kill the Select trigger's focus ring — InputGroup owns the outer ring */
  :deep(button[role='combobox']:focus),
  :deep(button[role='combobox']:focus-visible) {
    @apply outline-hidden! shadow-none! ring-0!;
  }
</style>
